import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Calculator, ArrowRightCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Quick price estimator — Pharmacy Staff types an address, picks a deliver type,
 * and gets the price + miles immediately. No order is created.
 *
 * Optionally calls onApply({address, deliver_type}) to copy the quoted values
 * straight into the New Order form.
 */
export default function PriceEstimator({ pharmacyId, defaultDeliverType = "same_day", onApply }) {
  const [address, setAddress] = useState("");
  const [deliverType, setDeliverType] = useState(defaultDeliverType);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!address.trim()) {
      toast.error("Type an address first");
      return;
    }
    if (!pharmacyId) {
      toast.error("No pharmacy selected");
      return;
    }
    setLoading(true);
    setQuote(null);
    try {
      const { data } = await api.get("/pricing/quote", {
        params: { address: address.trim(), pharmacy_id: pharmacyId, deliver_type: deliverType },
      });
      setQuote(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); run(); }
  };

  return (
    <Card className="p-5 rounded-xl border-border bg-card/80" data-testid="price-estimator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="h-4 w-4 text-primary" />
        <h3 className="font-display font-bold tracking-tight">Price estimator</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Type any address to see the driving miles + price <em>before</em> you create the order.
        Handy for quoting a patient on the phone.
      </p>

      <div className="space-y-3">
        <div>
          <Label htmlFor="est-addr" className="text-xs">Address</Label>
          <Input
            id="est-addr"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="123 Main St, City, State"
            data-testid="price-estimator-address"
          />
        </div>
        <div>
          <Label className="text-xs">Deliver type</Label>
          <Select value={deliverType} onValueChange={setDeliverType}>
            <SelectTrigger data-testid="price-estimator-deliver-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="same_day">Same Day</SelectItem>
              <SelectItem value="next_day">Next Day</SelectItem>
              <SelectItem value="otc">OTC</SelectItem>
              <SelectItem value="pickup">Pick Up</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          onClick={run}
          disabled={loading || !address.trim()}
          className="w-full rounded-full"
          data-testid="price-estimator-run"
        >
          {loading ? "Calculating…" : "Estimate price"}
        </Button>
      </div>

      {quote && (
        <div className="mt-5 pt-4 border-t border-border space-y-3" data-testid="price-estimator-result">
          <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">Result</div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Miles" value={`${quote.miles?.toFixed(1) ?? "—"} mi`} testId="price-estimator-miles" />
            <Stat
              label="Price"
              value={typeof quote.price === "number" ? `$${quote.price.toFixed(2)}` : "—"}
              accent
              testId="price-estimator-price"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            <div className="font-medium text-foreground truncate" title={quote.address}>{quote.address}</div>
            <div className="mt-1">
              {quote.has_pricing
                ? <>Band <span className="font-mono-num">{quote.band || "—"}</span> for <span className="capitalize">{quote.deliver_type?.replace("_", " ")}</span></>
                : <span className="text-amber-700">No pricing table set for this deliver type — price not available.</span>}
            </div>
          </div>
          {onApply && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onApply({ address: quote.address, deliver_type: quote.deliver_type })}
              className="w-full rounded-full"
              data-testid="price-estimator-apply"
            >
              <ArrowRightCircle className="h-4 w-4 mr-1.5" /> Use this address for the order
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-secondary/30"}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono-num text-lg ${accent ? "text-primary font-semibold" : "font-medium"}`}>{value}</div>
    </div>
  );
}
